import { LoadingButton } from '@mui/lab'
import {
  FormControl,
  FormHelperText,
  Grid,
  InputLabel,
  MenuItem,
  Select,
  TextField,
  Tooltip,
  Typography
} from '@mui/material'
import { FormikErrors, useFormikContext } from 'formik'
import React from 'react'
import { useToggle } from 'react-use'

import { comparators, types } from 'components/scenarios/utils/criteria-utils'
import { TestEvaluationResultsGoldenSignalIntegration } from 'components/services/create/TestEvaluationResultsGoldenSignalIntegration'
import useAuth from 'hooks/use-auth'
import toast from 'lib/toast'
import { EndpointResponse } from 'types/api/end-point'
import { EvaluationResults } from 'types/api/services/evaluation'
import {
  Predicate,
  ServiceGoldenSignal
} from 'types/api/services/golden-signal'
import {
  defaultStatusCheckEvaluationConfiguration,
  testStatusCheckEvaluation
} from 'utils/team-external-integrations/team-external-integrations-util'

export const TestEvaluationGoldenSignalIntegration: React.FC<{
  endpointResponse: EndpointResponse
  onChanged: (isEvaluationChecked: boolean) => void
}> = ({ endpointResponse, onChanged }) => {
  const form = useFormikContext<ServiceGoldenSignal>()
  const [isLoading, setIsLoading] = useToggle(false)
  const [evaluationResults, setEvaluationResults] =
    React.useState<EvaluationResults>()
  const { currentTeamId } = useAuth()

  React.useEffect(() => setEvaluationResults(null), [endpointResponse])

  const predicateType =
    form.values?.evaluationConfiguration?.responseBodyEvaluation
      ?.predicates?.[0]?.type
  const responseBodyEvaluationForm =
    form.values?.evaluationConfiguration?.responseBodyEvaluation

  const predicateErrors = form.errors?.evaluationConfiguration
    ?.responseBodyEvaluation?.predicates?.[0] as FormikErrors<Predicate>

  const touchedPredicate =
    form.touched?.evaluationConfiguration?.responseBodyEvaluation
      ?.predicates?.[0]

  const formRef = React.useRef(form)
  const resetEvaluationConfiguration = () => {
    formRef.current.setFieldValue(
      'evaluationConfiguration',
      defaultStatusCheckEvaluationConfiguration,
      true
    )
  }

  React.useEffect(() => {
    const { predicates } = responseBodyEvaluationForm
    if (predicates?.length > 0) {
      const { jpQuery, rValue } = predicates[0]
      if (jpQuery === '' && rValue === '') {
        resetEvaluationConfiguration()
      }
    }
  }, [responseBodyEvaluationForm])

  return (
    <Tooltip
      data-testid="service-status-check-evaluation-tooltip"
      followCursor={true}
      title={
        !endpointResponse
          ? 'The request needs to be tested first before evaluating'
          : ''
      }
    >
      <span style={{ width: '100%' }}>
        <Grid
          container
          data-test-test-evaluation-golden-signal-integration
          direction="column"
          rowSpacing={2}
        >
          <Grid item>
            <FormControl component="fieldset" variant="standard">
              <Typography
                data-test-test-evaluation-golden-signal-integration-header
              >
                Success Evaluation
              </Typography>
              <FormHelperText
                data-test-test-evaluation-golden-signal-integration-helper-text
              >
                This criteria will tell us if the Service is weathering the
                attack
              </FormHelperText>
            </FormControl>
          </Grid>
          <Grid item>
            <TextField
              data-testid="service-status-check-healthy-status-code"
              disabled={!endpointResponse}
              error={
                form.touched?.evaluationConfiguration?.healthyStatusCode &&
                Boolean(form.errors?.evaluationConfiguration?.healthyStatusCode)
              }
              helperText={
                (form.touched?.evaluationConfiguration?.healthyStatusCode &&
                  form.errors?.evaluationConfiguration?.healthyStatusCode) ||
                'Add a single code or a range; e.g. 200-299'
              }
              label="Healthy Status Code"
              name="evaluationConfiguration.healthyStatusCode"
              type="text"
              value={form.values.evaluationConfiguration.healthyStatusCode}
              onBlur={form.handleBlur}
              onChange={(e) => {
                form.handleChange(e)
                onChanged(false)
              }}
            />
          </Grid>
          <Grid item>
            <TextField
              data-testid="service-status-check-request-timeout"
              disabled={!endpointResponse}
              error={
                form.touched?.evaluationConfiguration?.requestTimeout &&
                Boolean(form.errors?.evaluationConfiguration?.requestTimeout)
              }
              helperText={
                (form.touched?.evaluationConfiguration?.requestTimeout &&
                  form.errors?.evaluationConfiguration?.requestTimeout) ||
                'Maximum time in milliseconds to wait for a response'
              }
              label="Request Timeout"
              name="evaluationConfiguration.requestTimeout"
              type="number"
              value={form.values.evaluationConfiguration.requestTimeout}
              onBlur={form.handleBlur}
              onChange={(e) => {
                form.handleChange(e)
                onChanged(false)
              }}
            />
          </Grid>
          {((endpointResponse?.contentType?.toLowerCase().indexOf('json') ??
            -1) !== -1 ||
            responseBodyEvaluationForm.predicates?.length > 0) && (
            <Grid container item direction="row" spacing="10">
              <Grid item>
                <TextField
                  data-testid="service-status-check-evaluation-predicate-key"
                  disabled={isLoading || !endpointResponse}
                  error={
                    touchedPredicate?.jpQuery &&
                    Boolean(predicateErrors?.jpQuery)
                  }
                  helperText={
                    predicateErrors?.jpQuery || 'Key from response body'
                  }
                  label="Key"
                  name="evaluationConfiguration.responseBodyEvaluation.predicates[0].jpQuery"
                  value={
                    responseBodyEvaluationForm?.predicates?.[0]?.jpQuery || ''
                  }
                  onBlur={form.handleBlur}
                  onChange={(e) => {
                    form.handleChange(e)
                    onChanged(false)
                  }}
                />
              </Grid>
              <Grid item>
                <FormControl
                  error={touchedPredicate && Boolean(predicateErrors?.type)}
                  sx={{ minWidth: 150 }}
                >
                  <InputLabel>Key Type</InputLabel>
                  <Select
                    data-testid="service-status-check-evaluation-predicate-type"
                    disabled={isLoading || !endpointResponse}
                    label="Key Type"
                    name="evaluationConfiguration.responseBodyEvaluation.predicates[0].type"
                    value={predicateType || ''}
                    onChange={(e) => {
                      form.handleChange(e)
                      onChanged(false)
                    }}
                  >
                    {Object.keys(types).map((predicateType) => (
                      <MenuItem
                        key={predicateType}
                        data-testid={`evaluationConfiguration.responseBodyEvaluation.predicates[0].type.${predicateType.toLowerCase()}`}
                        value={predicateType}
                      >
                        {predicateType}
                      </MenuItem>
                    ))}
                  </Select>
                  <FormHelperText>{predicateErrors?.type}</FormHelperText>
                </FormControl>
              </Grid>
              <Grid item>
                <FormControl
                  error={
                    touchedPredicate && Boolean(predicateErrors?.comparator)
                  }
                  sx={{ minWidth: 150 }}
                >
                  <InputLabel>Comparator</InputLabel>
                  <Select
                    data-testid="service-status-check-evaluation-predicate-key-comparator"
                    disabled={!predicateType || isLoading || !endpointResponse}
                    label="Key Type"
                    name="evaluationConfiguration.responseBodyEvaluation.predicates[0].comparator"
                    value={
                      responseBodyEvaluationForm?.predicates?.[0]?.comparator ||
                      ''
                    }
                    onChange={(e) => {
                      form.handleChange(e)
                      onChanged(false)
                    }}
                  >
                    {predicateType
                      ? types[predicateType].comparators.map((comparator) => (
                          <MenuItem
                            key={comparator}
                            data-testid={`service-status-check-evaluation-predicate-key-comparator-${comparator.toLowerCase()}`}
                            value={comparator}
                          >
                            {comparators[comparator].label}
                          </MenuItem>
                        ))
                      : []}
                  </Select>
                  <FormHelperText>{predicateErrors?.comparator}</FormHelperText>
                </FormControl>
              </Grid>
              <Grid item>
                <TextField
                  data-testid="service-status-check-evaluation-predicate-value"
                  disabled={isLoading || !endpointResponse}
                  error={touchedPredicate && Boolean(predicateErrors?.rValue)}
                  helperText={
                    predicateErrors?.rValue ||
                    'Success value from response body'
                  }
                  label="Value"
                  name="evaluationConfiguration.responseBodyEvaluation.predicates[0].rValue"
                  value={
                    responseBodyEvaluationForm?.predicates?.[0]?.rValue || ''
                  }
                  onBlur={form.handleBlur}
                  onChange={(e) => {
                    form.handleChange(e)
                    onChanged(false)
                  }}
                />
              </Grid>
            </Grid>
          )}
          <Grid item>
            <LoadingButton
              color="primary"
              data-testid="test-evaluation-status-check-integration-button"
              disabled={
                !endpointResponse ||
                Boolean(form.errors.evaluationConfiguration)
              }
              loading={isLoading}
              size="small"
              variant="contained"
              onClick={async () => {
                setIsLoading(true)
                await testStatusCheckEvaluation(
                  form.values.evaluationConfiguration,
                  endpointResponse,
                  currentTeamId,
                  null,
                  (evaluationResults) => {
                    setIsLoading(false)
                    setEvaluationResults(evaluationResults)
                    onChanged(true)
                  },
                  (err) => {
                    setIsLoading(false)
                    toast({ type: 'error', text: err?.message })
                  }
                )
              }}
            >
              Test Evaluation
            </LoadingButton>
          </Grid>
          <Grid item xs={12}>
            {!isLoading && evaluationResults && (
              <TestEvaluationResultsGoldenSignalIntegration
                endpointResponse={endpointResponse}
                evaluationResults={evaluationResults}
                integration={null}
              />
            )}
          </Grid>
        </Grid>
      </span>
    </Tooltip>
  )
}
